import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { MaterialRateAdvancedFiltersComponent } from '../material-rate-advanced-filters/material-rate-advanced-filters.component';
import { TagWithOverflowComponent } from '../../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { MasterDataManagementService } from '../../../../services/master-data-management.service';
import { ToastrService } from '../../../../services/toastr-service';
import { CodeDescription } from '../../../../models/code-description.model';
import { MaterialRateFilterForm, MaterialRateFiltersRawValue } from '../../../../form-model/material-rate-filter.form.model';
import { SharedDataService } from '../../../../services/shared-data.service';
import { AuthPolicies } from '../../../../auth/auth-policies';
import { AuthHelper } from '../../../../auth/auth-helper';
import { DownloadFilterCriteria, toFilter } from '../../../../models/download-filter.model';
import {
  markAsTagged as markFilterAsTagged,
  buildExpiryDateLabel,
  removeAtIndex as removeItemAtIndex,
  buildExpiryDateDownloadFilters,
} from '../../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../../helper/download-helpers';

import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';

type FilterValue = string | string[] | boolean | undefined | CodeDescription[];

@Component({
  selector: 'app-material-rates-filters',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, RouterModule, MaterialRateAdvancedFiltersComponent, TagWithOverflowComponent],
  templateUrl: './material-rate-filters.component.html',
  styleUrls: ['./material-rate-filters.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MaterialRatesFiltersComponent implements AfterViewInit, OnInit {
  readonly screenWidth = window.innerWidth;
  isGSCUser: boolean = AuthPolicies.isGSCUser();
  allShops = AuthHelper.user?.maintenanceShops ?? [];

  /** ✅ Directly take the FormGroup from parent */
  @Input() filterFormGroup!: FormGroup<MaterialRateFilterForm>;

  /** ✅ Notify parent when filters change */
  @Output() filtersChanged = new EventEmitter<MaterialRateFiltersRawValue>();

  showAdvanceFilters = false;
  savedFilter!: FormGroup<MaterialRateFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];

  @ViewChild(MaterialRateAdvancedFiltersComponent)
  advFilter!: MaterialRateAdvancedFiltersComponent;

  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly masterDataManagementService: MasterDataManagementService,
    private readonly cd: ChangeDetectorRef,
    private readonly sharedDataService: SharedDataService,
    public readonly authPolicies: AuthPolicies,
  ) {}

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }
  ngOnInit(): void {
    this.updateSelectedFiltersSection();
  }

  trackByKey(index: number, item: { key: string; value: any }) {
    return item.key;
  }

  /** ✅ Watch for Apply Filter trigger */
  private subscribeToFormChanges() {
    this.filterFormGroup.controls.applyMaterialRatesFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.sharedDataService.setMaterialRateFilterFormValue(this.filterFormGroup.value);
        this.filtersChanged.emit(this.filterFormGroup.getRawValue()); // ✅ send current filters to parent
        this.filterFormGroup.controls.applyMaterialRatesFilter.setValue(false, { emitEvent: false }); // prevent infinite loop
      }
    });
  }
  onApplyFilterClick(): void {
    this.updateSelectedFiltersSection();
    // Mark form as applied
    this.filterFormGroup.get('applyMaterialRatesFilter')?.setValue(true);
    this.sharedDataService.setMaterialRateFilterFormValue(this.filterFormGroup.value);
    // Emit filters to parent
    this.filtersChanged.emit(this.filterFormGroup.getRawValue());
  }

  /** ✅ Build tagged filters for display */
  updateSelectedFiltersSection() {
    const form = this.filterFormGroup.controls;
    this.taggedFilters = []; // clear previous

    markFilterAsTagged(this.taggedFilters, 'Region', form.regionCode?.value);
    markFilterAsTagged(this.taggedFilters, 'Cluster', form.areaCode?.value);
    markFilterAsTagged(this.taggedFilters, 'Country', form.countryCode?.value);
    markFilterAsTagged(this.taggedFilters, 'Shop Code', form.shopCode.value);
    markFilterAsTagged(this.taggedFilters, 'Repair Mode', form.repairMode.value);
    markFilterAsTagged(this.taggedFilters, 'Repair Code', form.repairCode?.value);

    const expiryLabel = buildExpiryDateLabel(
      form.expiryDateType.value,
      form.expiryDate.value,
      form.expiryDateFrom.value,
      form.expiryDateTo.value,
    );

    if (expiryLabel) {
      markFilterAsTagged(this.taggedFilters, 'Expiry Date', expiryLabel);
    }

    const effectiveDateLabel = buildExpiryDateLabel(
      form.effectiveDateType?.value,
      form.effectiveDate?.value,
      form.effectiveDateFrom?.value,
      form.effectiveDateTo?.value,
    );

    if (effectiveDateLabel) {
      markFilterAsTagged(this.taggedFilters, 'Effective Date', effectiveDateLabel);
    }
  }

  /** ✅ Open modal */
  showAdvanceFiltersModal() {
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
    // Sync parent form values to location-filter component's form
    this.syncToLocationFilter();
  }

  /** Synchronize parent form values to location-filter component's internal form */
  private syncToLocationFilter(): void {
    if (this.advFilter?.locationFilter) {
      const locationFormGroup = this.advFilter.locationFilter.getFormGroup();
      const form = this.filterFormGroup.controls;

      const regionCodes = form.regionCode?.value ?? [];
      const areaCodes = form.areaCode?.value ?? [];
      const countryCodes = form.countryCode?.value ?? [];
      const shopCodes = form.shopCode?.value ?? [];

      // Sync values from parent form to location-filter's form
      locationFormGroup.patchValue(
        {
          location: regionCodes,
          countrycluster: areaCodes,
          country: countryCodes,
          shopCode: shopCodes,
        },
        { emitEvent: false },
      );

      // Trigger cascading data loads if needed
      if (regionCodes.length > 0) {
        this.advFilter.locationFilter.getClusterByLoc(regionCodes).catch(() => {});
      }
      if (areaCodes.length > 0) {
        this.advFilter.locationFilter.getCountryByCluster(areaCodes).catch(() => {});
      }
      if (countryCodes.length > 0) {
        this.advFilter.locationFilter.getShopByCountry(countryCodes).catch(() => {});
      }

      // Update the selected shops display in location-filter
      if (shopCodes && shopCodes.length > 0) {
        this.advFilter.locationFilter.selectedShops = shopCodes.map((code) => ({ label: code, value: code }));
      } else {
        this.advFilter.locationFilter.selectedShops = [];
      }
    }
  }

  /** ✅ Save state for cancel */
  private saveCurrentFilters(): void {
    const form = this.filterFormGroup.controls;
    this.savedFilter = this.formBuilder.group({
      shopCode: this.formBuilder.control(form.shopCode.value),
      repairMode: this.formBuilder.control(form.repairMode.value),
      repairCode: this.formBuilder.control(form.repairCode?.value),
      expiryDateType: this.formBuilder.control(form.expiryDateType.value),
      expiryDateFrom: this.formBuilder.control(form.expiryDateFrom.value),
      expiryDateTo: this.formBuilder.control(form.expiryDateTo.value),
      expiryDate: this.formBuilder.control(form.expiryDate.value),
      effectiveDateType: this.formBuilder.control(form.effectiveDateType?.value),
      effectiveDateFrom: this.formBuilder.control(form.effectiveDateFrom?.value),
      effectiveDateTo: this.formBuilder.control(form.effectiveDateTo?.value),
      effectiveDate: this.formBuilder.control(form.effectiveDate?.value),
      regionCode: this.formBuilder.control(form.regionCode?.value),
      areaCode: this.formBuilder.control(form.areaCode?.value),
      countryCode: this.formBuilder.control(form.countryCode?.value),
      applyMaterialRatesFilter: this.formBuilder.control(form.applyMaterialRatesFilter.value),
    });
  }

  /** ✅ Close modal, restore old filters */
  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  /** ✅ Clear all filters */
  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.applyMaterialRatesFilter.setValue(true);
    this.updateSelectedFiltersSection();
    this.sharedDataService.setMaterialRateFilterFormValue(null);
    this.advFilter.resetFiltersForm();
    this.filtersChanged.emit();
  }

  unselectShopCode(): (shopCode: string) => void {
    return (shopCode: string) => {
      const shopCodeControl = this.filterFormGroup.controls.shopCode;
      const currentShopCodes = this.taggedFilters.find((f) => f.key === 'Shop Code')?.value;

      if (!currentShopCodes || !Array.isArray(currentShopCodes)) {
        return;
      }

      const updatedShopCodes = removeItemAtIndex(currentShopCodes as string[], (currentShopCodes as string[]).indexOf(shopCode));

      shopCodeControl.setValue(updatedShopCodes.length ? updatedShopCodes : null);
      this.filterFormGroup.controls.applyMaterialRatesFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectRegion() {
    return (region: string) => this.removeArrayFilter('Region', region, this.filterFormGroup.controls.regionCode);
  }

  unselectCluster() {
    return (cluster: string) => this.removeArrayFilter('Cluster', cluster, this.filterFormGroup.controls.areaCode);
  }

  unselectCountry() {
    return (country: string) => this.removeArrayFilter('Country', country, this.filterFormGroup.controls.countryCode);
  }

  unselectRepairMode() {
    return (repairMode: string) => this.removeArrayFilter('Repair Mode', repairMode, this.filterFormGroup.controls.repairMode);
  }

  unselectRepairCode() {
    return (repairCode: string) => this.removeArrayFilter('Repair Code', repairCode, this.filterFormGroup.controls.repairCode);
  }

  unselectExpiryDate() {
    return () => {
      const form = this.filterFormGroup.controls;
      form.expiryDateType.setValue(null);
      form.expiryDate.setValue(null);
      form.expiryDateFrom.setValue(null);
      form.expiryDateTo.setValue(null);
      form.applyMaterialRatesFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }
  /** Generic method to remove an item from array-type filter */
  private removeArrayFilter(key: string, value: string, control: any) {
    const filter = this.taggedFilters.find((f) => f.key === key);
    if (!filter || !Array.isArray(filter.value)) return;

    if (typeof filter.value[0] === 'string') {
      filter.value = (filter.value as string[]).filter((v) => v !== value);
    } else {
      filter.value = (filter.value as CodeDescription[]).filter((v) => v.code !== value);
    }

    control.setValue((filter.value as any).length ? filter.value : null);
    this.filterFormGroup.controls.applyMaterialRatesFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }
  unselectArrayFilter(taggedFilter: { key: string; value: FilterValue }, value: string | CodeDescription) {
    const control = this.filterFormGroup.controls[taggedFilter.key as keyof MaterialRateFilterForm];
    this.removeArrayFilter(taggedFilter.key, typeof value === 'string' ? value : value.code, control);
  }

  getFilterValueArray(taggedFilter: { key: string; value: any }): string[] | CodeDescription[] {
    if (!taggedFilter.value) return [];

    // if it's an array, check first element type
    if (Array.isArray(taggedFilter.value)) {
      return typeof taggedFilter.value[0] === 'string' ? (taggedFilter.value as string[]) : (taggedFilter.value as CodeDescription[]);
    }

    // single value
    return typeof taggedFilter.value === 'string' ? [taggedFilter.value] : [taggedFilter.value as CodeDescription];
  }

  /** 🔹 Get tagged Repair Modes */
  getTaggedRepairModes(): string[] {
    const tag = this.taggedFilters.find((t) => t.key === 'Repair Mode');
    return tag && Array.isArray(tag.value) ? (tag.value as string[]) : [];
  }

  /** 🔹 Get tagged Shop Codes */
  getTaggedShopCodes(): string[] {
    const tag = this.taggedFilters.find((t) => t.key === 'Shop Code');
    return tag && Array.isArray(tag.value) ? (tag.value as string[]) : [];
  }

  getTaggedRepairCodes(): string[] {
    const tag = this.taggedFilters.find((t) => t.key === 'Repair Code');
    return tag && Array.isArray(tag.value) ? (tag.value as string[]) : [];
  }

  /** 🔹 Get tagged Region Codes */
  getTaggedRegionCodes(): string[] {
    const tag = this.taggedFilters.find((t) => t.key === 'Region');
    return tag && Array.isArray(tag.value) ? (tag.value as string[]) : [];
  }

  /** 🔹 Get tagged Cluster/Area Codes */
  getTaggedAreaCodes(): string[] {
    const tag = this.taggedFilters.find((t) => t.key === 'Cluster');
    return tag && Array.isArray(tag.value) ? (tag.value as string[]) : [];
  }

  /** 🔹 Get tagged Country Codes */
  getTaggedCountryCodes(): string[] {
    const tag = this.taggedFilters.find((t) => t.key === 'Country');
    return tag && Array.isArray(tag.value) ? (tag.value as string[]) : [];
  }

  /** 🔹 Get tagged Expiry Date */
  getTaggedExpiryDate(): string {
    const tag = this.taggedFilters.find((t) => t.key === 'Expiry Date');
    return tag && typeof tag.value === 'string' ? tag.value : '';
  }

  onDownload() {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.masterDataManagementService.downloadMaterialRatesToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('shop-material-rates'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls;

    const regionCodes = form.regionCode?.value;
    if (regionCodes?.length) {
      filters.push(toFilter('region', regionCodes));
    }

    const areaCodes = form.areaCode?.value;
    if (areaCodes?.length) {
      filters.push(toFilter('cluster', areaCodes));
    }

    const countryCodes = form.countryCode?.value;
    if (countryCodes?.length) {
      filters.push(toFilter('country', countryCodes));
    }

    if (this.authPolicies.isShopUser) {
      const assignedShops = this.allShops.map((s) => s.maintenanceShopCode);
      if (assignedShops.length > 0) {
        filters.push(toFilter('shop_code', assignedShops));
      }
    } else {
      const shopCodes = form.shopCode?.value;
      if (shopCodes?.length) {
        filters.push(toFilter('shop_code', shopCodes));
      }
    }

    const repairModes = form.repairMode?.value;
    if (repairModes?.length) {
      filters.push(toFilter('repair_mode', repairModes));
    }

    const repairCodes = form.repairCode?.value;
    if (repairCodes?.length) {
      filters.push(toFilter('repair_code', repairCodes));
    }

    filters.push(...this.buildExpiryDateFilters());

    return filters;
  }

  private buildExpiryDateFilters(): DownloadFilterCriteria[] {
    const form = this.filterFormGroup.controls;
    return buildExpiryDateDownloadFilters(
      'expiry_date',
      form.expiryDateType?.value,
      form.expiryDate?.value,
      form.expiryDateFrom?.value,
      form.expiryDateTo?.value,
    );
  }

  isDownload = false;
}
